import { useMemo, useState } from "react";
import { SortColumn } from "react-data-grid";

import { ColorConstants, ProjectIssuanceConstants } from "../../../../constants";
import { CursorChangeProps, ResultType } from "../../../../models";
import { getProjectIssuances, GetProjectIssuancesResponse } from "../../../../service/publicquery";
import {
  getHabitatFromStandardSpecificIssuanceData,
  getInventoryChartShape,
  getInventoryColumns,
  getInventoryDefaultSort,
  getInventoryVerifiedColours,
  getInventoryVerifiedPredicate,
  getInventoryXAxisLabel,
  getNutrientChartColours,
  mapInventoryIssuancesToRows,
} from "../../../../utils";
import {
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
  StackedChartData,
} from "../../../../widget";
import { usePublicContext } from "../../usePublicContext";

interface UseInventoryReturnData {
  onChange: DataGridCursorDataLoadEventHandler;
  isLoading: boolean;
  hasProjectDetails: boolean;
  hasData: boolean;
  hasEnoughIssuancesForScrolling: boolean;
  columns: DataGridColumnDefinition[];
  unitMeasurement: string;
  stackedChartData: StackedChartData[] | undefined;
  showAvailable: boolean;
  xAxisLabels: (string | string[])[];
  chartKeyNutrients: string[];
  defaultSortingCriteria: SortColumn[];
}

export type PublicInventoryIssuance = GetProjectIssuancesResponse["results"][number];

export interface PublicInventoryChartData {
  xAxisLabels: (string | string[])[];
  stackedChartData: StackedChartData[];
  /**
   * Nutrient Neutrality only - the nutrients drawn, in bar order, so the chart key can render one
   * colour block per nutrient. Empty for every other measurement, which leaves the key unchanged.
   */
  chartKeyNutrients: string[];
}

const SURFACE = "public";

export const getDefaultSortingCriteria = (unitMeasurement: string): SortColumn[] => [
  { columnKey: getInventoryDefaultSort(SURFACE, unitMeasurement).gridColumnKey, direction: "ASC" },
];

export const buildPublicInventoryChartData = (
  results: PublicInventoryIssuance[],
  unitMeasurement: string,
  showAvailable: boolean
): PublicInventoryChartData => {
  const shape = getInventoryChartShape(SURFACE, unitMeasurement);
  const isVerifiedIssuance = getInventoryVerifiedPredicate(shape.verifiedBy);

  const keys: (string | string[])[] = [];
  const verifiedTotal: number[] = [];
  const verifiedAvailable: number[] = [];
  const pendingTotal: number[] = [];
  const pendingAvailable: number[] = [];

  /*
    Nutrient Neutrality: one bar per nutrient, aggregated - two issuances of the same nutrient fold
    into one column. Restoration takes this branch too, drawing one bar per unit type - in practice
    the single "30x30 Unit" - with the same verified-only fill. NN carries no vintage and no verification status, so both the vintage x-axis
    label and the verified/pending split below are meaningless for it; grouping here is what keeps
    every bar off the "undefined-undefined" label the Carbon branch produced for it.

    Only the verified pair is filled. For NN the return below drops the pending pair entirely rather
    than stacking two permanently empty series; Restoration keeps the standard four-series return.
  */
  if (shape.groupBy === "quantityType") {
    const groupedResults: {
      [nutrient: string]: {
        unitQuantityTotal: number;
        unitQuantityAvailable: number;
      };
    } = {};

    results.forEach((value) => {
      const nutrient = value.quantityType ?? "";

      if (!groupedResults[nutrient]) {
        groupedResults[nutrient] = {
          unitQuantityTotal: 0,
          unitQuantityAvailable: 0,
        };
      }

      groupedResults[nutrient].unitQuantityTotal += value.unitQuantityTotal;
      groupedResults[nutrient].unitQuantityAvailable += value.unitQuantityAvailable;
    });

    Object.keys(groupedResults).forEach((nutrient) => {
      keys.push(nutrient);
      verifiedTotal.push(groupedResults[nutrient].unitQuantityTotal);
      if (showAvailable) {
        verifiedAvailable.push(groupedResults[nutrient].unitQuantityAvailable);
      }
    });
  } else if (shape.groupBy === "habitat") {
    const groupedResults: {
      [key: string]: {
        unitQuantityTotal: number;
        unitQuantityAvailable: number;
      };
    } = {};

    results.forEach((value) => {
      const habitatType = getHabitatFromStandardSpecificIssuanceData(value.standardSpecificIssuanceData).join("-");

      if (!groupedResults[habitatType]) {
        groupedResults[habitatType] = {
          unitQuantityTotal: 0,
          unitQuantityAvailable: 0,
        };
      }

      groupedResults[habitatType].unitQuantityTotal += value.unitQuantityTotal;
      groupedResults[habitatType].unitQuantityAvailable += value.unitQuantityAvailable;
    });

    Object.keys(groupedResults).forEach((key: string) => {
      keys.push(key.split("-"));
      verifiedTotal.push(groupedResults[key].unitQuantityTotal);
      if (showAvailable) {
        verifiedAvailable.push(groupedResults[key].unitQuantityAvailable);
      }
    });
  } else {
    results.forEach((value) => {
      keys.push(getInventoryXAxisLabel(SURFACE, value));
      const isVerified = isVerifiedIssuance(value);
      verifiedTotal.push(isVerified ? value.unitQuantityTotal : 0);
      pendingTotal.push(isVerified ? 0 : value.unitQuantityTotal);
      if (showAvailable) {
        verifiedAvailable.push(isVerified ? value.unitQuantityAvailable : 0);
        pendingAvailable.push(isVerified ? 0 : value.unitQuantityAvailable);
      }
    });
  }

  const pendingTotalChart = pendingTotal.map((total, i) =>
    showAvailable && total === pendingAvailable[i] ? 0 : total - (showAvailable ? pendingAvailable[i] : 0)
  );

  const verifiedTotalChart = verifiedTotal.map((total, i) =>
    showAvailable && total === verifiedAvailable[i] ? 0 : total - (showAvailable ? verifiedAvailable[i] : 0)
  );

  /*
    Nutrient Neutrality stacks Allocated and Available only. The four-series shape below splits on a
    verification status the measurement does not declare, which would class every NN unit pending and
    draw the whole chart in the INDIGO pending family while advertising a split that cannot exist.

    Each nutrient carries its own colour family rather than the shared GREEN pair, matching the
    authenticated project chart: a series' colour varies per bar, which Chart.js supports by taking
    an array of colours indexed by category. The palettes come from getNutrientChartColours, so the
    bars and the key cannot drift apart.

    Gated on the measurement as well as the shape: Restoration shares the shape but has no nutrients,
    so it falls through to the standard Allocated / Available series in its own colour family.
  */
  if (
    shape.verifiedBy === "none" &&
    shape.groupBy === "quantityType" &&
    unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY
  ) {
    const nutrients = keys as string[];
    const palettes = nutrients.map((nutrient, index) => getNutrientChartColours(nutrient, index));

    return {
      xAxisLabels: keys,
      chartKeyNutrients: nutrients,
      stackedChartData: [
        {
          label: "Allocated",
          data: verifiedTotalChart,
          backgroundColor: palettes.map((palette) => palette.dark),
          hoverBackgroundColor: palettes.map((palette) => palette.darkHover),
        },
        {
          label: "Available",
          data: verifiedAvailable,
          backgroundColor: palettes.map((palette) => palette.light),
          hoverBackgroundColor: palettes.map((palette) => palette.lightHover),
        },
      ],
    };
  }

  const verifiedColours = getInventoryVerifiedColours(unitMeasurement);

  return {
    xAxisLabels: keys,
    chartKeyNutrients: [],
    stackedChartData: [
      {
        label: "Allocated",
        data: pendingTotalChart,
        backgroundColor: ColorConstants.INDIGO_500,
        hoverBackgroundColor: ColorConstants.INDIGO_400,
      },
      {
        label: "Available",
        data: pendingAvailable,
        backgroundColor: ColorConstants.INDIGO_100,
        hoverBackgroundColor: ColorConstants.BLUE_100,
      },
      {
        label: "Allocated",
        data: verifiedTotalChart,
        backgroundColor: verifiedColours.dark,
        hoverBackgroundColor: verifiedColours.darkHover,
      },
      {
        label: "Available",
        data: verifiedAvailable,
        backgroundColor: verifiedColours.light,
        hoverBackgroundColor: verifiedColours.lightHover,
      },
    ],
  };
};

export const useInventory = (): UseInventoryReturnData => {
  const { projectDetails } = usePublicContext();
  const showPrice = !!projectDetails && projectDetails.listing?.showPriceInDirectory;
  const showAvailable = !!projectDetails && projectDetails.listing?.availableForSale;
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasEnoughIssuancesForScrolling, setHasEnoughIssuancesForScrolling] = useState(false);

  const [stackedChartData, setStackedChartData] = useState<StackedChartData[]>();
  const [xAxisLabels, setXAxisLabels] = useState<(string | string[])[]>([]);
  const [chartKeyNutrients, setChartKeyNutrients] = useState<string[]>([]);

  const unitMeasurement = useMemo(
    () => projectDetails?.unitMeasurements?.[0] ?? ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_CARBON,
    [projectDetails?.unitMeasurements]
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    if (!projectDetails) {
      return data;
    }
    setIsLoading(true);

    const sortKeyMap: Record<string, string> = {
      vintage: "vintageStartDate",
    };

    await getProjectIssuances({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: paging.pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      /* eslint-enable */
      filter: { results: filterCriteria },
      projectUuid: projectDetails?.uuid,
    })
      .then((response) => {
        const results = response.data?.results ?? [];
        data = {
          resultData: mapInventoryIssuancesToRows(results, unitMeasurement),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };

        const chart = buildPublicInventoryChartData(results, unitMeasurement, showAvailable);

        setHasData(results.length > 0);
        setHasEnoughIssuancesForScrolling(results.length > 5);
        setXAxisLabels(chart.xAxisLabels);
        setStackedChartData(chart.stackedChartData);
        setChartKeyNutrients(chart.chartKeyNutrients);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return data;
  };

  return {
    onChange,
    isLoading,
    hasData,
    hasEnoughIssuancesForScrolling,
    hasProjectDetails: !!projectDetails,
    columns: getInventoryColumns(unitMeasurement, showPrice, showAvailable),
    unitMeasurement,
    xAxisLabels,
    showAvailable,
    stackedChartData,
    chartKeyNutrients,
    defaultSortingCriteria: getDefaultSortingCriteria(unitMeasurement),
  };
};
